import { Box, FloatingHelper, Text, TextButton } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'
import React, { FC, useCallback, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { usePageLogger } from '../../../bi/mfi'
import { LARGE_DESKTOP } from '../../../breakpoints'
import { Button } from '../../reusable/Button'
import { LogoWithBack } from '../../reusable/LogoWithBack'
import { RichText } from '../../reusable/RichText'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import { WixStudioTemplateSidePanel } from '../../reusable/WixStudioTemplate/WixStudioTemplateSidePanel'
import { Content } from './Content'
import { dataHooks } from './WixStudioBulletSplit.dataHooks'

export interface WixStudioBulletSplitProps {
  markAsWixStudioAccount: () => Promise<void>
  unmarkAsWixStudioAccount: () => Promise<void>
}

export const WixStudioBulletSplit: FC<WixStudioBulletSplitProps> = ({
  markAsWixStudioAccount,
  unmarkAsWixStudioAccount,
}) => {
  const { content } = useContent<Content>()
  const { questionKey, options } = content.question
  const next = useNext()
  const pageLogger = usePageLogger()
  const [isPageLoading] = usePageLoading()
  const isLargeDesktopScreen = useMediaQuery(LARGE_DESKTOP)
  const [isPrimaryButtonClicked, setPrimaryButtonClicked] = useState<boolean>(false)
  const [isSecondaryButtonClicked, setSecondaryButtonClicked] = useState<boolean>(false)
  const [helperOpenedTimes, setHelperOpenedTimes] = useState(0)

  const [primaryOption, secondaryOption, learnMore] = options
  const learnMoreOptionKey = learnMore?.optionKey
  const incrementHelperOpenedTimes = () => {
    setHelperOpenedTimes(prev => prev + 1)
  }

  const handlePrimaryOptionClick = useCallback(async () => {
    if (isPageLoading) {
      return
    }
    setPrimaryButtonClicked(true)
    void pageLogger.answer({ answer: primaryOption.optionKey })
    if (primaryOption.markAsStudio) {
      const [error] = await tryCatch(markAsWixStudioAccount)
      if (error) {
        window.open('https://www.wix.com/studio/templates', '_self')
        return
      }
    } else if (primaryOption.unmarkAsStudio) {
      const [error] = await tryCatch(unmarkAsWixStudioAccount)
      if (error) {
        window.open('https://www.wix.com/studio/templates', '_self')
        return
      }
    }
    next({ [questionKey]: { values: [primaryOption.optionKey] } })
  }, [isPageLoading, markAsWixStudioAccount, next, pageLogger, primaryOption, questionKey])

  const handleSecondaryOptionClick = useCallback(async () => {
    if (isPageLoading) {
      return
    }
    setSecondaryButtonClicked(true)
    void pageLogger.answer({ answer: secondaryOption.optionKey })
    if (secondaryOption.markAsStudio) {
      const [error] = await tryCatch(markAsWixStudioAccount)
      if (error) {
        window.open('https://www.wix.com/studio/templates', '_self')
        return
      }
    } else if (secondaryOption.unmarkAsStudio) {
      const [error] = await tryCatch(unmarkAsWixStudioAccount)
      if (error) {
        window.open('https://www.wix.com/studio/templates', '_self')
        return
      }
    }
    next({ [questionKey]: { values: [secondaryOption.optionKey] } })
  }, [isPageLoading, markAsWixStudioAccount, next, pageLogger, questionKey, secondaryOption])

  const handleLearMoreButtonClick = useCallback(() => {
    if (isPageLoading) {
      return
    }
    void pageLogger.generalAction({ action: learnMoreOptionKey })
    next({ [questionKey]: { values: [learnMoreOptionKey] } })
  }, [isPageLoading, learnMoreOptionKey, next, pageLogger, questionKey])

  const handleBack = useCallback(async () => {
    await pageLogger.back()
    window.history.back()
  }, [pageLogger])

  return (
    <WixStudioTemplate
      logo={
        content.logo === 'WIX' ? <LogoWithBack onBackClick={handleBack} backButton={content.backButton} /> : undefined
      }
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      backButton={content.backButton}
      sidePanel={<WixStudioTemplateSidePanel content={[content.sidePanel]} />}
      title={
        <Box maxWidth="500px" marginBottom="SP4">
          {content.title}
        </Box>
      }
      bottomNote={
        content.note && (
          <Box maxWidth="600px">
            <RichText size="small" secondary dataHook={dataHooks.note()}>
              {content.note}
            </RichText>
          </Box>
        )
      }
    >
      {learnMore && (
        <Box position="fixed" top="36px" right="36px" zIndex="1">
          <Button
            as="button"
            size="tiny"
            priority="secondary"
            label={learnMore.label}
            onClick={handleLearMoreButtonClick}
            dataHook={dataHooks.learnMore()}
          />
        </Box>
      )}
      <Box marginBottom="SP6">
        <Text listStyle="checkmark">
          <ul>
            {content.bullets.items.map((item, index) => (
              <li key={index} data-hook={dataHooks.bullets({ id: index })}>
                {item.text}
              </li>
            ))}
          </ul>
        </Text>
      </Box>
      <Box gap={content.secondaryOptionAppearance === 'Button' ? 'SP2' : 'SP3'} verticalAlign="middle">
        <Button
          label={primaryOption.label}
          skin="standard"
          isLoading={isPrimaryButtonClicked}
          onClick={handlePrimaryOptionClick}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          dataHook={dataHooks.mainButton()}
        />
        {content.secondaryOptionAppearance === 'Button' ? (
          <Button
            label={secondaryOption.label}
            skin="standard"
            priority="secondary"
            isLoading={isSecondaryButtonClicked}
            onClick={handleSecondaryOptionClick}
            size={isLargeDesktopScreen ? 'large' : 'medium'}
            dataHook={dataHooks.secondaryButton()}
          />
        ) : (
          <Box verticalAlign="middle" gap="10px">
            <TextButton
              onClick={handleSecondaryOptionClick}
              skin="dark"
              underline="always"
              dataHook={dataHooks.secondaryTextButton()}
              onMouseEnter={() => {
                if (helperOpenedTimes < 1) {
                  incrementHelperOpenedTimes()
                }
              }}
            >
              {secondaryOption.label}
            </TextButton>
            {content.popover?.content?.isVisible && (
              <FloatingHelper
                opened={helperOpenedTimes === 1}
                target={<span />}
                onClose={incrementHelperOpenedTimes}
                content={
                  <FloatingHelper.Content
                    title={content.popover.content.title}
                    body={
                      <Box direction="vertical">
                        <Text light>{content.popover.content.description}</Text>
                        <TextButton
                          skin="light"
                          underline="always"
                          as="a"
                          target="_blank"
                          href={content.popover.content.url}
                        >
                          {content.popover.content.urlText}
                        </TextButton>
                      </Box>
                    }
                    actionText={content.popover.content.btn}
                    onActionClick={incrementHelperOpenedTimes}
                  />
                }
                placement="right"
              />
            )}
          </Box>
        )}
      </Box>
    </WixStudioTemplate>
  )
}
